import React, { useState, useMemo, useEffect, useRef } from 'react';
import './ApprovalsStyles.css';
import { FaCheck, FaTimes, FaRedo, FaChevronDown, FaSearch, FaEye } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { useExpenseSheetDate } from './context/DateContext.js';
import { confirm } from './confirmWindow/confirm.js';

const API_BASE_URL = process.env.REACT_APP_EZTS_API_BASE_URL;

const statusMap = {
  1: "draft",
  2: "pending",
  3: "approved",
  4: "rejected",
};

/**
 * Least important first — hidden as the table container shrinks.
 * Always kept: status, employee, amount, date, actions.
 */
const COLUMN_PRIORITY = [
  { key: 'expenseType', className: 'col-p4', maxWidth: 1000 },
  { key: 'title', className: 'col-p3', maxWidth: 860 },
  { key: 'project', className: 'col-p2', maxWidth: 740 },
  { key: 'id', className: 'col-p1', maxWidth: 620 },
];

const ExpenseApprovals = () => {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'descending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInputs, setSearchInputs] = useState({});
  const [searchVisible, setSearchVisible] = useState({});
  const rowsPerPage = 5;

  const [expApiError, setExpApiError] = useState(false);
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState([]);
  const tableContainerRef = useRef(null);

  const { ApprExpStartDate, 
          ApprExpSetStartDate, 
          ApprExpEndDate, 
          ApprExpSetEndDate, 
          ApprExpHasFetched, 
          ApprExpSetHasFetched } = useExpenseSheetDate() || {};

  const fetchExpenses = async (from = "", to = "") => {
    if (!user) return;
    try {
      const response = await axios.get(
        `${API_BASE_URL}expense/all?userId=&projectId=&from=${from}&to=${to}&orgId=${user.department?.costCenter?.organization?.orgId}&approverId=${user.userId}`
      );

      const apiData = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : [];
      console.log("Expense Sheet API response:", apiData);

      const mapped = apiData.map(expense => ({
        id: expense.expenseId,
        status: statusMap[expense.status] || "unknown",
        employee: `${expense.user?.firstName || ""} ${expense.user?.lastName || ""}`.trim() || "-",
        title: expense.user?.title?.titleName || "-",
        project: expense.user?.projects?.[0]?.projectCode || "-",
        expenseType: expense.expenseTypeId || "-",
        expenseAmount: expense.expenseAmount || 0,
        expenseDate: expense.expenseDate,
      }));
      

      setExpenses(mapped);
      setFilteredExpenses(null);
      ApprExpSetHasFetched(true);
    } catch (error) {
      console.error("Error fetching expense sheets:", error);
      setExpApiError(true);
      setTimeout(() => setExpApiError(false), 1500);
    }
  }; 

  const sortedExpenses = useMemo(() => {
    let sortableItems = filteredExpenses ? [...filteredExpenses] : [...expenses];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        if (sortConfig.key === 'approver') {
          aValue = aValue || '';
          bValue = bValue || '';
        }
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [expenses, filteredExpenses, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedExpenses.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRows = sortedExpenses.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const requestSort = (key) => {
    let direction = 'descending';
    if (sortConfig.key === key && sortConfig.direction === 'descending') {
      direction = 'ascending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
// Not sorted: show both up and down faded
    if (sortConfig.key !== key) {
      return (
        <span className="table-sort-arrows">
          <FaChevronDown className="table-sort-arrow up faded margin-bottom" />
          <FaChevronDown className="table-sort-arrow faded margin-top" />
        </span>
      );
    }
    // Sorted: show only one arrow, centered, rotate if ascending
    const isAsc = sortConfig.direction === 'ascending';
    return (
      <FaChevronDown
        className={`table-sort-arrow centered${isAsc ? ' up' : ''}`}
      />
    );
  };

  const handleApplyFilter = () => {
      const from = ApprExpStartDate ? ApprExpStartDate.toISOString().split("T")[0] : "";
      const to = ApprExpEndDate ? ApprExpEndDate.toISOString().split("T")[0] : "";
      fetchExpenses(from, to);
  };
  
  // Auto-load approvals when the page is opened (empty dates = all).
  useEffect(() => {
    if (!user) return;
    handleApplyFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return undefined;

    const syncFromWidth = (width) => {
      const nextHidden = COLUMN_PRIORITY.filter((col) => width <= col.maxWidth).map(
        (col) => col.key
      );
      setHiddenColumnKeys((prev) =>
        prev.length === nextHidden.length && prev.every((key, i) => key === nextHidden[i])
          ? prev
          : nextHidden
      );
    };

    syncFromWidth(el.clientWidth);

    if (typeof ResizeObserver !== 'function') {
      const onResize = () => syncFromWidth(el.clientWidth);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const width = entry?.contentRect?.width ?? el.clientWidth;
      syncFromWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSearchIconClick = (key) => {
    setSearchVisible((prev) => {
      // Close all popovers first, then open the clicked one
      const allClosed = Object.keys(prev).reduce((acc, k) => {
        acc[k] = false;
        return acc;
      }, {});
      
      // If the clicked popover was already open, keep it closed
      // If it was closed, open it
      return { ...allClosed, [key]: !prev[key] };
    });
  };

  const handleSearchInputChange = (key, value) => {
    setSearchInputs((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearchInputKeyDown = (key, e) => {
    if (e.key === 'Enter') {
      handleSearch(key);
    } else if (e.key === 'Escape') {
      setSearchVisible((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSearch = (key) => {
    const value = searchInputs[key] || '';
    setFilteredExpenses(
      expenses.filter(expenses =>
        String(expenses[key] || '').toLowerCase().includes(value.toLowerCase())
      )
    );
    setCurrentPage(1);
    setSearchVisible((prev) => ({ ...prev, [key]: false }));
  };

  const handleCloseSearch = (key) => {
    setSearchVisible((prev) => ({ ...prev, [key]: false }));
  };

  // Actions
  // Approve button handler (asks for confirmation)
  const handleApprove = (id) => {
    if (!user) return;
    confirm.open({
      title: t("approvals.approveConfTitle"),
      message: t("approvals.approveExpConfMsg"),
      onConfirm: async () => {
        try {
          await axios.put(
            `${API_BASE_URL}expense/status?expenseId=${id}&status=3`,
            null
          );
          handleApplyFilter();
        } catch (error) {
          console.error("Error approving expense sheet " + id + ":", error);
          setExpApiError(true);
          setTimeout(() => setExpApiError(false), 1500);
        }
      },
      onCancel: () => {}
    });
  };


  // Reject button handler (asks for confirmation)
  const handleReject = (id) => {
    if (!user) return;
    confirm.open({
      title: t("approvals.rejectConfTitle"),
      message: t("approvals.rejectExpConfMsg"),
      onConfirm: async () => {
        try {
          await axios.put(
            `${API_BASE_URL}expense/status?expenseId=${id}&status=4`,
            null
          );
          handleApplyFilter();
        } catch (error) {
          console.error("Error rejecting expense sheet " + id + ":", error);
          setExpApiError(true);
          setTimeout(() => setExpApiError(false), 1500);
        }
      },
      onCancel: () => {}
    });
  };

  // View button handler
  const handleView = (id) => {
    // Implement actual reject logic here
    navigate(`/approvals/expenseapprovals/view/${id}`);
  };

  // Render sortable header with search
  const renderSortableHeader = (key, titleKey, extraClass = '') => (
    <th className={`table-header sortable${extraClass ? ` ${extraClass}` : ''}`}>
      <span className="table-header-inner">
        <span className="table-header-label" onClick={() => requestSort(key)}>
          <span className="table-header-label-text">{t(`approvals.${titleKey}`)}</span>
          {getSortIcon(key)}
        </span>
        <span className="table-header-search">
          {searchVisible[key] && (
            <div className="table-search-popover">
              <input
                type="text"
                className="table-search-input"
                value={searchInputs[key] || ''}
                autoFocus
                onChange={e => handleSearchInputChange(key, e.target.value)}
                onKeyDown={e => handleSearchInputKeyDown(key, e)}
                placeholder={t('approvals.searchPlaceholder', { field: t(`approvals.${titleKey}`) })}
              />
              <div className="table-search-btn-row">
                <button className="table-search-btn" onClick={() => handleSearch(key)}>
                  <FaSearch style={{ fontSize: '16px' }} />
                </button>
                <button className="table-search-close-btn" onClick={() => handleCloseSearch(key)}>
                  <IoMdClose style={{ fontSize: '18px' }} />
                </button>
              </div>
            </div>
          )}
          <FaSearch
            className="table-search-icon"
            style={{ cursor: 'pointer', marginLeft: '4px', fontSize: '14px' }}
            onClick={() => handleSearchIconClick(key)}
          />
        </span>
      </span>
    </th>
  );

  const hiddenColumns = COLUMN_PRIORITY.filter((col) =>
    hiddenColumnKeys.includes(col.key)
  );
  const tableClassName = [
    'expenseapprovals-table',
    ...hiddenColumns.map((col) => `hide-${col.className}`),
  ].join(' ');

  return (
    <div className="approvals-container">
      {/* Header */}
      <div className="approvals-header-container" >
        <div className="approvals-header" >
          <h1 className="approvals-title">{t("approvals.expenseApprovals")}</h1>
        </div>
        <div className="approvals-tab-container">
          <nav className="approvals-tab-list">
            <NavLink
              key="/approvals/timesheetapprovals"
              to="/approvals/timesheetapprovals"
              className={({ isActive }) => 
                  `approvals-tab-item ${isActive ? 'active' : ''}`
                }
            >
              {t("approvals.timesheetApprovals")}
            </NavLink>
            <NavLink
              key="/approvals/expenseapprovals"
              to="/approvals/expenseapprovals"
              className={({ isActive }) => 
                  `approvals-tab-item ${isActive ? 'active' : ''}`
                }
            >
              {t("approvals.expenseApprovals")}
            </NavLink>
          </nav>
        </div>
        {expApiError && <h3 className="api-error-text">{t("approvals.apiFailure")}</h3>}
      </div>

      {/* Filters — match timesheet list layout */}
      <div className="filters-wrapper">
        <div className="filters-container">
          <div className="filter-controls">
            <div className="filter-dates-row">
              <div className="filter-group">
                <label className="filter-label">{t("approvals.from")}</label>
                <DatePicker
                  selected={ApprExpStartDate}
                  onChange={date => ApprExpSetStartDate(date)}
                  selectsStart
                  startDate={ApprExpStartDate}
                  endDate={ApprExpEndDate}
                  placeholderText={t("approvals.selectStartDate")}
                  className="date-input"
                />
              </div>
              <div className="filter-group">
                <label className="filter-label filter-label-to">{t("approvals.to")}</label>
                <DatePicker
                  selected={ApprExpEndDate}
                  onChange={date => ApprExpSetEndDate(date)}
                  selectsEnd
                  startDate={ApprExpStartDate}
                  endDate={ApprExpEndDate}
                  minDate={ApprExpStartDate}
                  placeholderText={t("approvals.selectEndDate")}
                  className="date-input"
                />
              </div>
            </div>
            <button className="apply-filter-button" onClick={handleApplyFilter}>
              {t("approvals.search")}
            </button>
          </div>
        </div>
      </div>
      
      {/* Table */}
      <div className="table-container" ref={tableContainerRef}>
        <table className={tableClassName}>
          <thead>
            <tr>
              {renderSortableHeader('status', 'status')}
              {renderSortableHeader('id', 'id', 'col-p1')}
              {renderSortableHeader('employee', 'employee')}
              {renderSortableHeader('title', 'title', 'col-p3')}
              {renderSortableHeader('project', 'project', 'col-p2')}
              {renderSortableHeader('expenseType', 'expenseType', 'col-p4')}
              {renderSortableHeader('amount', 'amount')}
              {renderSortableHeader('date', 'expenseDate')}
              <th className="table-header">{t("approvals.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.map((expense) => (
              <tr key={expense.id}>
                <td className="table-cell">
                  <span className={`status-badge ${expense.status}`}>
                    {t(`approvals.${expense.status}`, expense.status.charAt(0).toUpperCase() + expense.status.slice(1))}
                  </span>
                </td>
                <td className="table-cell col-p1">{expense.id}</td>
                <td className="table-cell">{expense.employee}</td>
                <td className="table-cell col-p3">{expense.title}</td>
                <td className="table-cell col-p2">{expense.project}</td>
                <td className="table-cell col-p4">{expense.expenseType}</td>
                <td className="table-cell">{expense.expenseAmount}</td>
                <td className="table-cell">{expense.expenseDate}</td>
                <td className="table-cell actions-cell">
                  <button className="action-button view" onClick={() => handleView(expense.id)}>
                    <FaEye /> {t("approvals.view")}
                  </button>
                  {expense.status === 'pending' && (
                    <button className="action-button approve" onClick={() => handleApprove(expense.id)}>
                      <FaCheck /> {t("approvals.approve")}
                    </button>
                  )}
                  {(expense.status === 'pending' || expense.status === 'approved') && (
                    <button className="action-button reject" onClick={() => handleReject(expense.id)}>
                      <FaTimes /> {t("approvals.reject")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pagination">
        <button 
          onClick={handlePreviousPage} 
          disabled={currentPage === 1}
          className="pagination-arrow"
          aria-label={t("approvals.previous")}
        >
          &lt;
        </button>
        <input
          type="number"
          min={1}
          max={totalPages}
          value={currentPage}
          onChange={e => {
            let page = Number(e.target.value);
            if (page < 1) page = 1;
            if (page > totalPages) page = totalPages;
            setCurrentPage(page);
          }}
          className="pagination-input"
        />
        <span className="pagination-total">
          {t("approvals.of")} {totalPages}
        </span>
        <button 
          onClick={handleNextPage} 
          disabled={currentPage === totalPages}
          className="pagination-arrow"
          aria-label={t("approvals.next")}
        >
          &gt;
        </button>
      </div>
    </div>
  );
};

export default ExpenseApprovals;